import React, { useState, useEffect } from 'react';
import { useSettings } from '../../context/SettingsContext';
import en from '../../locales/en.json';
import hi from '../../locales/hi.json';
import gu from '../../locales/gu.json';
import { Card, Btn as Button, Badge, SectionHeader, Avatar } from '../../components/ui';
import axios from '../../api/axios';
import AddEditVisitorServiceProviderModal from './AddEditVisitorServiceProviderModal'; 
import ViewVisitorServiceProviderModal from './ViewVisitorServiceProviderModal';

const translations = { en, hi, gu };

const VisitorServiceProviders = () => {
    const { language = 'en' } = useSettings();
    const t = (key, params) => {
        let str = translations[language]?.service_providers?.[key] || translations.en?.service_providers?.[key] || key;
        if (params) { Object.keys(params).forEach(p => { str = str.replace(`{${p}}`, params[p]); }); }
        return str;
    };
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providerToEdit, setProviderToEdit] = useState(null);

    useEffect(() => {
        fetchProviders();
    }, []);

    const fetchProviders = async () => {
        try {
            const response = await axios.get('/api/v1/visitor-service-providers');
            setProviders(response.data || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching providers:', error);
            setLoading(false);
        }
    };

    if (loading) return <div className="fade-up p-4 text-center text-muted text-sm">{t("loading")}</div>;

    return (
        <div className="fade-up space-y-4">
            <SectionHeader
                title={t("title_service_providers")}
                subtitle={t("desc_service_providers")}
                action={<Button sm variant="primary" icon="plus" onClick={() => setIsAddModalOpen(true)}>{t("btn_add_new")}</Button>}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {providers.map(provider => (
                    <Card key={provider.id} hover className="flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <Avatar name={provider.name} src={provider.photo_url} size={44} />
                                <div className="min-w-0 flex-1">
                                    <h3 className="font-bold text-sm sm:text-base text-ink dark:text-white truncate" title={provider.name}>
                                        {provider.name}
                                    </h3>
                                    <p className="text-xs text-muted capitalize truncate">
                                        {t("type_" + (provider.service_type || '').toLowerCase().replace(/ /g, "_")) || provider.service_type}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-2 py-2.5 border-y border-slate-100 dark:border-slate-800 text-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-muted">{t("label_status")}</span>
                                    <Badge
                                        color={
                                            provider.units?.[0]?.access_status === 'enabled' ? 'green' : 
                                            (provider.units?.[0]?.access_status === 'expired' ? 'amber' : 'red')
                                        }
                                        dot
                                        sm
                                    >
                                        {provider.units?.[0]?.access_status === 'enabled' ? 'Enabled' : 
                                         (provider.units?.[0]?.access_status === 'expired' ? 'Expired' : 'Disabled')}
                                    </Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-muted">{t("label_mobile")}</span>
                                    <span className="font-medium text-slate-700 dark:text-slate-200 font-mono">
                                        {provider.mobile_number || "-"}
                                    </span>
                                </div>
                                {provider.entries && provider.entries.length > 0 && (
                                    <div className="flex justify-between items-center pt-1">
                                        <span className="text-muted">{t("label_last_entry")}</span>
                                        <span className="text-slate-700 dark:text-slate-300">
                                            {new Date(provider.entries[0].entry_time).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mt-3.5 pt-0.5 flex items-center gap-2">
                            <Button sm variant="default" className="flex-1" onClick={() => setProviderToEdit(provider)}>{t("btn_edit")}</Button>
                            <Button sm variant="secondary" className="flex-1" onClick={() => setSelectedProvider(provider)}>{t("btn_view_details")}</Button>
                        </div>
                    </Card>
                ))}
            </div>

            {providers.length === 0 && !loading && (
                <div className="col-span-full py-8 text-center text-muted text-sm bg-slate-50/50 dark:bg-slate-800/50 rounded-xl border border-dashed border-border">
                    {t("msg_no_providers_added")}
                </div>
            )}

            {isAddModalOpen && (
                <AddEditVisitorServiceProviderModal
                    onClose={() => setIsAddModalOpen(false)}
                    onSuccess={fetchProviders}
                />
            )}

            {providerToEdit && (
                <AddEditVisitorServiceProviderModal
                    providerToEdit={providerToEdit}
                    onClose={() => setProviderToEdit(null)}
                    onSuccess={fetchProviders}
                />
            )}

            {selectedProvider && (
                <ViewVisitorServiceProviderModal
                    provider={selectedProvider}
                    onClose={() => setSelectedProvider(null)}
                />
            )}
        </div>
    );
};

export default VisitorServiceProviders;
